import type { CoverArtPresignBody, CropRect } from '@@/types/api/common';
import { computeAutoCrop, defaultCoverArtState } from '~/utils/cover-art-crop';

// ---------------------------------------------------------------------------
// Cover-art form state
//
// Host-agnostic: the shape and every mutation live here as pure functions so
// both the Pinia event-form store and the lightweight `useCoverArt()`
// composable can share one implementation. Nothing in this module imports Vue.
// ---------------------------------------------------------------------------

export type CoverArtRuntimeState =
  | 'idle' // no image chosen
  | 'uploading' // presign obtained, S3 PUT in flight
  | 'processing' // PUT done, waiting for arvee-media webhook
  | 'ready' // derivatives available
  | 'failed'; // dispatcher failed

export interface CoverArtState {
  mediaAssetId: string | null; // ULID once presign returns
  originalUrl: string | null; // blob: URL during create, signed URL during edit
  focalPoint: { x: number; y: number };
  natural: { w: number; h: number }; // oriented pixel dims of the source image; {0,0} until known
  crops: { '16_9': CropRect; '1_1': CropRect };
  overrides: { '16_9': boolean; '1_1': boolean };
  runtimeState: CoverArtRuntimeState;
  serverState: 'processing' | 'ready' | 'failed' | null; // last-known state from the API resource; null when the subject has no cover image
}

export function initialCoverArtState(): CoverArtState {
  return {
    mediaAssetId: null,
    originalUrl: null,
    ...defaultCoverArtState(),
    runtimeState: 'idle',
    serverState: null,
  };
}

/**
 * Update focal point; always recompute BOTH aspect crops and clear any
 * previously-set overrides. In the toggle-mode UX, focal drag is only
 * possible in focal mode, and dragging focal is the explicit "auto-crop
 * from focal" gesture — it always applies and always wins. Users who want
 * per-aspect manual control switch to Advanced mode.
 */
export function applyFocalPoint(
  state: CoverArtState,
  focal: { x: number; y: number },
): void {
  state.focalPoint = focal;
  const nat = state.natural;
  state.crops['16_9'] = computeAutoCrop(focal, { w: 16, h: 9 }, nat);
  state.crops['1_1'] = computeAutoCrop(focal, { w: 1, h: 1 }, nat);
  state.overrides['16_9'] = false;
  state.overrides['1_1'] = false;
}

/**
 * Record the oriented pixel dimensions of the source image and recompute
 * any non-overridden crops. Called from CoverImageUpload once
 * `createImageBitmap` (create flow) or the <img> load event (edit flow)
 * reports real dimensions.
 */
export function applyNaturalDimensions(
  state: CoverArtState,
  dims: { w: number; h: number },
): void {
  state.natural = dims;
  if (!state.overrides['16_9']) {
    state.crops['16_9'] = computeAutoCrop(
      state.focalPoint,
      { w: 16, h: 9 },
      dims,
    );
  }
  if (!state.overrides['1_1']) {
    state.crops['1_1'] = computeAutoCrop(
      state.focalPoint,
      { w: 1, h: 1 },
      dims,
    );
  }
}

/** Set an override crop; marks that aspect as manually adjusted. */
export function applyOverrideCrop(
  state: CoverArtState,
  aspect: '16_9' | '1_1',
  rect: CropRect,
): void {
  state.crops[aspect] = rect;
  state.overrides[aspect] = true;
}

/** Reset one aspect back to the focal-point auto-crop. */
export function applyResetAspectOverride(
  state: CoverArtState,
  aspect: '16_9' | '1_1',
): void {
  state.overrides[aspect] = false;
  const targetAspect = aspect === '16_9' ? { w: 16, h: 9 } : { w: 1, h: 1 };
  state.crops[aspect] = computeAutoCrop(
    state.focalPoint,
    targetAspect,
    state.natural,
  );
}

/** Revoke a `blob:` object URL, ignoring anything else (signed URLs, null). */
export function revokeBlobUrl(url: string | null | undefined): void {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

/**
 * Build the shared cover-art presign request body.
 *
 * Key order and names are load-bearing: the backend runs pixel-space aspect
 * validation of `crops` against `natural` and 422s on any mismatch. Do not
 * rename or drop keys here — host-specific identifiers (`event_id`, or an
 * id baked into the URL path) are the transport's business, not this body's.
 */
export function toCoverArtPresignBody(
  state: CoverArtState,
  file: Pick<File, 'type' | 'size'>,
): CoverArtPresignBody {
  return {
    mime_type: file.type,
    byte_size: file.size,
    natural: state.natural,
    focal_point: state.focalPoint,
    crops: state.crops,
    overrides: state.overrides,
  };
}
